import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, AlertTriangle, BarChart, User, MessageSquare, Activity, Star, Settings } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';
import { connectivityService, ConnectivityResult } from '../../lib/connectivityService';

// Import new modular components
import ContactInfoCard from '../../components/admin/prospect/ContactInfoCard';
import QuizResponsesCard from '../../components/admin/prospect/QuizResponsesCard';
import TestLinkCard from '../../components/admin/prospect/TestLinkCard';
import NotesCard from '../../components/admin/prospect/NotesCard';
import PageSpeedAnalysisCard from '../../components/admin/prospect/PageSpeedAnalysisCard';
import CmsDetectionCard from '../../components/admin/prospect/CmsDetectionCard';
import KeywordAnalysisCard from '../../components/admin/prospect/KeywordAnalysisCard';
import GoogleMyBusinessCard from '../../components/admin/prospect/GoogleMyBusinessCard';
import ProspectEditForm from '../../components/admin/prospect/ProspectEditForm';
import QuickScanButton from '../../components/admin/prospect/QuickScanButton';
import StatusBadge, { getPageStatus, getQuizStatus, getAppointmentStatus } from '../../components/common/StatusBadge';

// Import shared types
import { Prospect, QuizResponse, PageSpeedResult } from '../../components/admin/prospect/types';

const ProspectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [responses, setResponses] = useState<QuizResponse[]>([]);
  const [connectivityResult, setConnectivityResult] = useState<ConnectivityResult | null>(null);
  const [pagespeedResults, setPagespeedResults] = useState<PageSpeedResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCheckingConnectivity, setIsCheckingConnectivity] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Configuration des onglets
  const tabs = [
    {
      id: 'overview',
      name: 'Vue d\'ensemble',
      icon: User,
      description: 'Informations de contact et détails'
    },
    {
      id: 'quiz',
      name: 'Quiz & Réponses',
      icon: MessageSquare,
      description: 'Réponses au questionnaire',
      count: responses.length
    },
    {
      id: 'analysis',
      name: 'Analyses techniques',
      icon: Activity,
      description: 'PageSpeed, CMS, SEO'
    },
    {
      id: 'business',
      name: 'Business & Avis',
      icon: Star,
      description: 'Google My Business et notes'
    },
    {
      id: 'actions',
      name: 'Actions & Outils',
      icon: Settings,
      description: 'Tests et analyses avancées'
    }
  ];

  // Fonction pour vérifier la connectivité du site
  const checkSiteConnectivity = async () => {
    if (!prospect?.site_url) return;
    
    setIsCheckingConnectivity(true);
    try {
      const urlToCheck = connectivityService.normalizeUrl(prospect.site_url);
      const connectivity = await connectivityService.checkConnectivityWithFallback(urlToCheck);
      setConnectivityResult(connectivity);
    } catch (error) {
      console.error('Erreur lors de la vérification de connectivité:', error);
      setConnectivityResult({
        isAccessible: false,
        statusCode: undefined,
        error: 'Erreur de vérification'
      });
    } finally {
      setIsCheckingConnectivity(false);
    }
  };

  useEffect(() => {
    const fetchProspectData = async () => {
      if (!id) return;

      const { data: prospectData } = await supabase
        .from('prospects')
        .select('*')
        .eq('id', id)
        .single();

      if (prospectData) {
        setProspect(prospectData);

        const { data: responsesData } = await supabase
          .from('reponses_quiz')
          .select('*')
          .eq('token', prospectData.token)
          .order('timestamp', { ascending: true });

        if (responsesData) {
          setResponses(responsesData);
        }
        
        // Rechercher les résultats PageSpeed existants
        try {
          const { data: pagespeedData, error: pagespeedError } = await supabase
            .from('pagespeed_results')
            .select('*')
            .eq('prospect_id', id)
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (pagespeedError) {
            console.error('Erreur lors de la récupération des résultats d\'analyse:', pagespeedError);
            // Ne pas afficher d'erreur à l'utilisateur
          } else if (pagespeedData && pagespeedData.length > 0) {
            setPagespeedResults(pagespeedData[0]);
          }
        } catch (err) {
          console.error('Exception lors de la récupération des résultats d\'analyse:', err);
          // Ne pas propager l'erreur à l'UI
        }
        
        // Rechercher les résultats de détection CMS existants
        try {
          // Utiliser une approche plus robuste pour gérer les erreurs potentielles
          const { data: cmsData, error: cmsError } = await supabase
            .from('cms_detection_results')
            .select('*')
            .eq('prospect_id', id)
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (cmsError) {
            console.error('Erreur lors de la récupération des détections CMS:', cmsError);
            // Ne pas afficher d'erreur à l'utilisateur et continuer le flux
            // L'erreur 406 sera loggée mais n'interrompra pas l'expérience utilisateur
          } else if (cmsData && cmsData.length > 0) {
          }
        } catch (err) {
          console.error('Exception lors de la récupération des détections CMS:', err);
          // Ne pas propager l'erreur à l'UI
        }
      }
    };

    fetchProspectData();
    // La vérification de connectivité n'est plus automatique
  }, [id]);

  if (!prospect) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="max-w-3xl mx-auto">
            <p>Loading...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Fonction pour supprimer le prospect
  const deleteProspect = async () => {
    if (!id || !prospect) return;
    
    try {
      // Bloquer toute tentative de création pendant la suppression
      setIsLoading(true);
      
      const prospectName = `${prospect.prenom} ${prospect.nom}`.trim();

      // Approche simplifiée et robuste : supprimer directement le prospect sans archivage préalable
      // pour garantir la suppression effective
      
      // 1. Suppression des données associées d'abord
      // IMPORTANT : Toutes les tables avec foreign key vers prospects doivent être supprimées :
      // - keyword_analysis_results, cms_detection_results, pagespeed_results
      // - google_reviews_results, personalized_intros, ai_recommendations, ai_citations_results
      // - dataforseo_keywords, instant_page_analysis, seo_audit_requests
      // - notes, reponses_quiz (via token)
      console.log('🗑️ Début de la suppression des données associées...');
      
      // Suppression des analyses de mots-clés
      const { error: keywordError } = await supabase
        .from('keyword_analysis_results')
        .delete()
        .eq('prospect_id', id);
      
      if (keywordError) {
        console.error('Erreur lors de la suppression des analyses de mots-clés:', keywordError);
      }
        
      // Suppression des détections CMS
      const { error: cmsError } = await supabase
        .from('cms_detection_results')
        .delete()
        .eq('prospect_id', id);
      
      if (cmsError) {
        console.error('Erreur lors de la suppression des détections CMS:', cmsError);
      }
        
      // Suppression des résultats PageSpeed
      const { error: pageSpeedError } = await supabase
        .from('pagespeed_results')
        .delete()
        .eq('prospect_id', id);
      
      if (pageSpeedError) {
        console.error('Erreur lors de la suppression des résultats PageSpeed:', pageSpeedError);
      }
      
      // Suppression des Google Reviews
      const { error: googleReviewsError } = await supabase
        .from('google_reviews_results')
        .delete()
        .eq('prospect_id', id);
      
      if (googleReviewsError) {
        console.error('Erreur lors de la suppression des Google Reviews:', googleReviewsError);
      }
      
      // Suppression des introductions personnalisées
      const { error: introsError } = await supabase
        .from('personalized_intros')
        .delete()
        .eq('prospect_id', id);
      
      if (introsError) {
        console.error('Erreur lors de la suppression des introductions:', introsError);
      }
      
      // Suppression des recommandations AI
      const { error: aiRecoError } = await supabase
        .from('ai_recommendations')
        .delete()
        .eq('prospect_id', id);
      
      if (aiRecoError) {
        console.error('Erreur lors de la suppression des recommandations AI:', aiRecoError);
      }
      
      // Suppression des citations AI
      const { error: aiCitationsError } = await supabase
        .from('ai_citations_results')
        .delete()
        .eq('prospect_id', id);
      
      if (aiCitationsError) {
        console.error('Erreur lors de la suppression des citations AI:', aiCitationsError);
      }
      
      // Suppression des mots-clés DataForSEO
      const { error: dataforSeoError } = await supabase
        .from('dataforseo_keywords')
        .delete()
        .eq('prospect_id', id);
      
      if (dataforSeoError) {
        console.error('Erreur lors de la suppression des mots-clés DataForSEO:', dataforSeoError);
      }
      
      // Suppression des analyses instantanées
      const { error: instantAnalysisError } = await supabase
        .from('instant_page_analysis')
        .delete()
        .eq('prospect_id', id);
      
      if (instantAnalysisError) {
        console.error('Erreur lors de la suppression des analyses instantanées:', instantAnalysisError);
      }
      
      // Suppression des demandes d'audit SEO
      const { error: seoAuditError } = await supabase
        .from('seo_audit_requests')
        .delete()
        .eq('prospect_id', id);
      
      if (seoAuditError) {
        console.error('Erreur lors de la suppression des demandes d\'audit SEO:', seoAuditError);
      }
        
      // Suppression des notes
      const { error: notesError } = await supabase
        .from('notes')
        .delete()
        .eq('prospect_id', id);
      
      if (notesError) {
        console.error('Erreur lors de la suppression des notes:', notesError);
      }
        
      // Suppression des réponses au quiz
      if (prospect?.token) {
        const { error: quizError } = await supabase
          .from('reponses_quiz')
          .delete()
          .eq('token', prospect.token);
        
        if (quizError) {
          console.error('Erreur lors de la suppression des réponses au quiz:', quizError);
        }
      }
      
      console.log('✅ Toutes les données associées supprimées');
      
      // 2. Suppression du prospect lui-même (étape finale)
      const { error: deleteError } = await supabase
        .from('prospects')
        .delete()
        .eq('id', id);
      
      if (deleteError) {
        console.error('Erreur lors de la suppression du prospect:', deleteError);
        throw new Error(`Erreur lors de la suppression du prospect: ${deleteError.message}`);
      }

      // 3. Vérification que le prospect a bien été supprimé
      const { data: checkData } = await supabase
        .from('prospects')
        .select('id')
        .eq('id', id)
        .single();
      
      if (checkData) {
        throw new Error(`Échec de la suppression: le prospect existe toujours en base de données`);
      }

      
      // Navigation vers la liste avec notification de succès
      navigate('/mon-espace/prospects', { 
        state: { 
          deleteSuccess: true,
          deletedProspectName: prospectName
        }
      });
      
    } catch (error) {
      console.error('Erreur lors de la suppression du prospect:', error);
      
      // Afficher un message d'erreur plus informatif
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert(`Une erreur est survenue lors de la suppression du prospect: ${errorMessage}`);
      
      // Réactiver l'interface en cas d'erreur
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Modal de confirmation de suppression */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div 
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                aria-hidden="true"
                onClick={() => !isLoading && setIsDeleteModalOpen(false)}
              ></div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Confirmer la suppression
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Êtes-vous sûr de vouloir supprimer le prospect <strong>{prospect?.prenom} {prospect?.nom}</strong> ?
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Cette action est irréversible. Toutes les données associées (analyses, notes, résultats de quiz) seront archivées puis supprimées.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={isLoading}
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${
                      isLoading 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                    }`}
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      deleteProspect();
                    }}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Suppression...
                      </div>
                    ) : (
                      'Supprimer définitivement'
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={isLoading}
                    className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 sm:mt-0 sm:w-auto sm:text-sm ${
                      isLoading 
                        ? 'cursor-not-allowed opacity-50' 
                        : 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                    }`}
                    onClick={() => !isLoading && setIsDeleteModalOpen(false)}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Header fixe avec informations principales */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <Link
                  to="/mon-espace/prospects"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Link>
                <div className="h-6 w-px bg-gray-300" />
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">
                    {prospect.prenom} {prospect.nom}
                  </h1>
                  <p className="text-sm text-gray-500">{prospect.metadata?.company || 'Entreprise non renseignée'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  {/* Status badges avec le composant commun */}
                  <StatusBadge type={getPageStatus(prospect.page_vue)} />
                  <StatusBadge type={getQuizStatus(
                    !!prospect.token,
                    !!prospect.quiz_commence,
                    !!prospect.quiz_en_cours,
                    !!prospect.quiz_fait
                  )} />
                  <StatusBadge type={getAppointmentStatus(!!prospect.rdv_pris)} />
                </div>
                {/* Quick Scan Button */}
                <QuickScanButton 
                  siteUrl={prospect.site_url} 
                  prospectId={prospect.id}
                  onScanComplete={() => {
                    // Rafraîchir les données après le scan
                    window.location.reload();
                  }}
                />
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={isLoading}
                  className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    isLoading 
                      ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                      : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                  }`}
                  title={isLoading ? 'Suppression en cours...' : 'Supprimer ce prospect'}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {isLoading ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation par onglets */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isDisabled = tab.id === 'quiz' && responses.length === 0;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => !isDisabled && setActiveTab(tab.id)}
                    disabled={isDisabled}
                    className={`
                      group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${isActive
                        ? 'border-blue-500 text-blue-600'
                        : isDisabled
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className={`
                      -ml-0.5 mr-2 h-5 w-5
                      ${isActive ? 'text-blue-500' : isDisabled ? 'text-gray-400' : 'text-gray-400 group-hover:text-gray-500'}
                    `} />
                    {tab.name}
                    {tab.count !== undefined && tab.count > 0 && (
                      <span className={`
                        ml-2 py-0.5 px-2 rounded-full text-xs font-medium
                        ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}
                      `}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Contenu des onglets */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Onglet Vue d'ensemble */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Informations de contact */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de contact</h3>
                <ContactInfoCard 
                  prospect={prospect}
                  connectivityResult={connectivityResult}
                  setConnectivityResult={setConnectivityResult}
                  setPagespeedResults={setPagespeedResults}
                  isAnalyzing={isAnalyzing}
                  setIsAnalyzing={setIsAnalyzing}
                  isCheckingConnectivity={isCheckingConnectivity}
                  setIsCheckingConnectivity={setIsCheckingConnectivity}
                  onEdit={() => setIsEditModalOpen(true)}
                  onCheckConnectivity={checkSiteConnectivity}
                />
              </div>

              {/* Informations détaillées */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Détails du prospect</h3>
                <div className="space-y-4">
                  {prospect.metadata?.company && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Entreprise</dt>
                      <dd className="text-base text-gray-900">{prospect.metadata.company}</dd>
                    </div>
                  )}
                  {(prospect.metadata?.title || prospect.metadata?.poste) && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Poste</dt>
                      <dd className="text-base text-gray-900">{prospect.metadata?.title || prospect.metadata?.poste}</dd>
                    </div>
                  )}
                  {prospect.metadata?.industry && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Secteur</dt>
                      <dd className="text-base text-gray-900">{prospect.metadata.industry}</dd>
                    </div>
                  )}
                  {prospect.metadata?.location && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Localisation</dt>
                      <dd className="text-base text-gray-900">{prospect.metadata.location}</dd>
                    </div>
                  )}
                  {prospect.source && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Source</dt>
                      <dd className="text-base text-gray-900">{prospect.source}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Ajouté le</dt>
                    <dd className="text-base text-gray-900">
                      {new Date(prospect.created_at).toLocaleDateString('fr-FR')}
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Onglet Quiz & Réponses */}
          {activeTab === 'quiz' && responses.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Réponses au questionnaire</h3>
              <QuizResponsesCard quizResponses={responses} />
            </div>
          )}

          {/* Onglet Analyses techniques */}
          {activeTab === 'analysis' && (
            <div className="space-y-6">
              {/* Analyses techniques en layout horizontal */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance PageSpeed</h3>
                  <PageSpeedAnalysisCard pagespeedResults={pagespeedResults} />
                </div>
                
                {prospect.site_url && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Détection CMS</h3>
                    <CmsDetectionCard siteUrl={prospect.site_url} prospectId={prospect.id} />
                  </div>
                )}
              </div>
              
              {/* Analyse SEO et mots-clés */}
              {prospect.site_url && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Analyse des mots-clés</h3>
                  <KeywordAnalysisCard siteUrl={prospect.site_url} prospectId={prospect.id} />
                </div>
              )}
            </div>
          )}

          {/* Onglet Business & Avis */}
          {activeTab === 'business' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Avis Google Business */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <GoogleMyBusinessCard 
                  prospect={prospect}
                  onRefresh={() => {
                    // Rafraîchir les données après actualisation des avis
                    window.location.reload();
                  }}
                />
              </div>
              
              {/* Notes */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
                <NotesCard 
                  prospect={prospect} 
                  onProspectUpdate={setProspect}
                />
              </div>
            </div>
          )}

          {/* Onglet Actions & Outils */}
          {activeTab === 'actions' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Actions */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
                <TestLinkCard prospect={prospect} />
              </div>

              {/* Analyse de Performance de Page */}
              {prospect.site_url && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <BarChart className="w-5 h-5" /> Performance de Page
                    </h3>
                  </div>
                  
                  <div>
                    <div className="text-gray-600 mb-4">
                      Analysez en détail les performances techniques et SEO de la page d'accueil du site web.
                    </div>
                    <div className="flex">
                      <Link 
                        to={`/prospects/${prospect.id}/page-performance/${encodeURIComponent(prospect.site_url)}`}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors bg-blue-50 text-blue-700 hover:bg-blue-100"
                      >
                        Lancer l'analyse de performance
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal d'édition du prospect */}
      {prospect && (
        <ProspectEditForm
          prospect={prospect}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={(updatedProspect) => {
            setProspect(updatedProspect);
            setIsEditModalOpen(false);
          }}
        />
      )}
    </AdminLayout>
  );
};

export default ProspectDetail;
